import { Fragment, memo, useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import {
  checkSentimentStatus,
  getDottedQuestionName,
  getNestedSentiment,
  getQuestionOptions,
  getSentiment,
  hasNestedQuestions,
  isVideoOrImageType,
  isXpertOrVirtualType,
  pathQuestionIdToOld
} from '@aytm/app/stats/page/comments/lib/utils'
import OEQuestionTitle from '@aytm/app/stats/page/comments/comments/components/OEQuestionTitle'
import OEQuestionResponse from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse'
import style from '@aytm/app/stats/page/comments/comments/styles/OEItem.module.scss'
import { DEFAULT_SELECTED_QUESTION, PIPING_MASK_REGEX } from '@aytm/app/stats/page/comments/lib/constants'
import { OEContext } from '@aytm/app/stats/page/comments/index'

const OEItem = memo(
  ({
    dataId,
    responseId,
    path,
    answer,
    survey,
    question,
    tags,
    selectedQuestion,
    pipingMask,
    toolBoxOptions,
    isRejected,
    voiceBusy,
    setVoiceBusy,
    onUpdateSchema,
    onUpdateParams
  }) => {
    const { imagesData, searchState } = useContext(OEContext)
    const { sentimentOptions, isQAMode, isShowAllTools, isShowSentiments, isTagsManagementDisabled } = searchState

    const isVirtualOrXpert = isXpertOrVirtualType(question)
    const isVideoOrImage = isVideoOrImageType(question)

    const { best_span_str } =
      useMemo(
        () => !isTagsManagementDisabled && (tags || []).find(t => 'comprehension' == t.type),
        [tags, isTagsManagementDisabled]
      ) || {}
    const questionOptions = useMemo(() => getQuestionOptions(question, survey, false, true), [survey, question])

    const renderQuestionWithAnswers = (node, path, showTitle = false) => {
      const nodeData = node.data ? node.data : node
      const nodeOptions = questionOptions.find(item => item.value && item.value.replace(/sq(\d+)/i, 'b$1') == path)
      if (!nodeOptions || !nodeOptions.value) return null

      const itemPath = pathQuestionIdToOld(getDottedQuestionName(path))
      const itemTags = tags.filter(t => t.path == itemPath)
      const itemHasNestedQuestions = hasNestedQuestions(questionOptions, nodeOptions, selectedQuestion)

      let sentimentStatus = checkSentimentStatus(itemPath, sentimentOptions)
      let sentiment = isVirtualOrXpert || isVideoOrImage || !sentimentStatus ? null : getSentiment(itemPath, itemTags)

      if (null === sentiment) {
        sentiment = getNestedSentiment(tags, itemPath, nodeData, sentimentOptions)
        if (false == sentiment || isVideoOrImage) {
          sentiment = { tag: '' }
          sentimentStatus = false
        }
        if (null === sentiment) sentiment = { tag: '' }
      }

      const nodeQuestionPath = nodeOptions.value
      if (selectedQuestion !== DEFAULT_SELECTED_QUESTION) {
        let selectedQuestionSubstring = selectedQuestion.slice(0, nodeQuestionPath.length)
        if (-1 == nodeQuestionPath.indexOf(selectedQuestionSubstring)) {
          return null
        } else if (nodeQuestionPath.length > selectedQuestion.length) {
          selectedQuestionSubstring = nodeQuestionPath.slice(selectedQuestion.length, selectedQuestion.length + 1)
          if (!Number.isNaN(Number(selectedQuestionSubstring))) return null
        } else if (nodeQuestionPath.length < selectedQuestion.length) {
          selectedQuestionSubstring = selectedQuestion.slice(nodeQuestionPath.length, nodeQuestionPath.length + 1)
          if (!Number.isNaN(Number(selectedQuestionSubstring))) return null
        }
      }

      const hasPiping =
        (pipingMask &&
          Object.hasOwn(pipingMask, `___${nodeOptions.value}t`) &&
          null !== pipingMask[`___${nodeOptions.value}t`]) ||
        PIPING_MASK_REGEX.test(nodeOptions.questionText)
      const hasTitle =
        !nodeOptions.isOptionHidden &&
        (showTitle ||
          selectedQuestion == DEFAULT_SELECTED_QUESTION ||
          (selectedQuestion != DEFAULT_SELECTED_QUESTION &&
            ((selectedQuestion != nodeOptions.value && itemHasNestedQuestions) || hasPiping)))
      const hasHeader =
        !nodeOptions.isOptionHidden &&
        (selectedQuestion == DEFAULT_SELECTED_QUESTION ||
          (selectedQuestion != DEFAULT_SELECTED_QUESTION && !itemHasNestedQuestions && !hasPiping))

      const showNestedTitle =
        showTitle ||
        (!nodeOptions.isOptionHidden &&
          (showTitle ||
            selectedQuestion == DEFAULT_SELECTED_QUESTION ||
            (selectedQuestion != DEFAULT_SELECTED_QUESTION && (itemHasNestedQuestions || hasPiping))))

      return (
        <Fragment key={`${responseId}-question-item-${itemPath}-${nodeOptions.lvl}`}>
          <div
            className={classNames(style['comment-body-item'], {
              [style['no-header']]: !showTitle && !hasHeader
            })}
          >
            {hasTitle || (!nodeOptions.lvl && !nodeOptions.isOptionHidden) ? (
              <div
                key={`OEQuestionTitle-${responseId}-${itemPath}-${nodeOptions.lvl}-cont`}
                className={classNames(style['comment-header'], style[`question-offset-${nodeOptions.lvl}`], {
                  [style['active']]: hasTitle && !isQAMode && isShowAllTools,
                  [style[`sentiment-${sentiment.tag || 'None'}`]]: sentimentStatus && isShowSentiments
                })}
              >
                {hasTitle ? (
                  <OEQuestionTitle
                    key={`OEQuestionTitle-${responseId}-${itemPath}-${nodeOptions.lvl}-inner`}
                    pipingMask={pipingMask}
                    questionText={nodeOptions.questionText}
                    nodeOptions={nodeOptions}
                    imagesData={imagesData}
                  />
                ) : null}
              </div>
            ) : null}
            {(nodeData.t || nodeData.v || nodeData.i) && (
              <OEQuestionResponse
                key={`OEQuestionResponse-${responseId}-${itemPath}-${nodeOptions.lvl}`}
                path={itemPath}
                responseId={responseId}
                dataId={dataId}
                answer={answer}
                question={question}
                node={nodeData}
                nodeOptions={nodeOptions}
                toolBoxOptions={toolBoxOptions}
                tags={itemTags}
                best_span_str={best_span_str}
                sentiment={sentiment.tag ? sentiment.tag : 'None'}
                hasTitle={
                  hasTitle ||
                  selectedQuestion == DEFAULT_SELECTED_QUESTION ||
                  (!hasTitle && ((selectedQuestion != nodeOptions.value && itemHasNestedQuestions) || hasPiping))
                }
                voiceBusy={voiceBusy}
                isVirtualOrXpert={isVirtualOrXpert}
                isVideoOrImage={isVideoOrImage}
                isRejected={isRejected}
                setVoiceBusy={setVoiceBusy}
                onUpdateSchema={onUpdateSchema}
                onUpdateParams={onUpdateParams}
              />
            )}
          </div>
          {nodeData.t || nodeData.v || nodeData.i
            ? (selectedQuestion == DEFAULT_SELECTED_QUESTION ||
                (selectedQuestion !== DEFAULT_SELECTED_QUESTION && selectedQuestion !== nodeOptions.value)) &&
              Object.keys(nodeData)
                .filter(itemKey => !['t', 'te', 'i', 'v', 'timings'].includes(itemKey))
                .map(itemKey => renderQuestionWithAnswers(nodeData[itemKey], `${path}${itemKey}`, showNestedTitle))
            : Object.keys(nodeData).map(itemKey =>
                renderQuestionWithAnswers(nodeData[itemKey], `${path}${itemKey}`, showNestedTitle)
              )}
        </Fragment>
      )
    }

    return (
      <div className={style['comment-container']} data-id={dataId}>
        <div className={style['comment-body']}>{renderQuestionWithAnswers(answer, path)}</div>
      </div>
    )
  }
)

OEItem.propTypes = {
  dataId: PropTypes.string,
  responseId: PropTypes.number,
  path: PropTypes.string,
  answer: PropTypes.object,
  survey: PropTypes.object,
  question: PropTypes.object,
  tags: PropTypes.array,
  selectedQuestion: PropTypes.string,
  pipingMask: PropTypes.object,
  toolBoxOptions: PropTypes.array,
  isRejected: PropTypes.bool,
  voiceBusy: PropTypes.bool,
  setVoiceBusy: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onUpdateParams: PropTypes.func
}

OEItem.displayName = 'OEItem'

export default OEItem
